import { DynamicStore } from "./types";
import { makeAutoObservable, reaction, IReactionDisposer } from "mobx";

type StoreReadyCallback = (store: DynamicStore) => void;

export class StoreRegistry {
  private stores = new Map<string, DynamicStore>();
  private reactions = new Map<string, IReactionDisposer>();
  private storeReadyCallbacks: StoreReadyCallback[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  onStoreReady(callback: StoreReadyCallback): void {
    this.storeReadyCallbacks.push(callback);
  }

  private notifyStoreReady(store: DynamicStore): void {
    this.storeReadyCallbacks.forEach(callback => callback(store));
  }

  private setupLifecycleHooks(store: any, path: string): void {
    if (!store.status) return;

    const dispose = reaction(
      () => store.status,
      (status) => {
        if (status === "ok" && store.breadcrumbLabel) {
          this.notifyStoreReady(store);
        }
      }
    );

    this.reactions.set(path, dispose);
  }

  getStore<T extends DynamicStore>(path: string, creator: () => T): T {
    if (!this.stores.has(path)) {
      const store = creator();
      this.stores.set(path, store);
      this.setupLifecycleHooks(store, path);
      return store;
    }
    return this.stores.get(path) as T;
  }

  hasStore(path: string): boolean {
    return this.stores.has(path);
  }

  getStoreIfExists(path: string): DynamicStore | undefined {
    return this.stores.get(path);
  }

  destroyStore(path: string): void {
    const store = this.stores.get(path);

    // Don't destroy stores waiting for callback (e.g., when user clicks "Add new")
    if (store?.isWaitingForCallback) {
      return;
    }

    const dispose = this.reactions.get(path);
    if (dispose) {
      dispose();
      this.reactions.delete(path);
    }

    if (store?.destroy) {
      store.destroy();
    }
    this.stores.delete(path);
  }

  // Force destroy even if waiting for callback
  forceDestroyStore(path: string): void {
    const store = this.stores.get(path);
    if (store) {
      store.isWaitingForCallback = false;
    }
    this.destroyStore(path);
  }

  // Find store by storeId (used for callback processing)
  getStoreByStoreId(storeId: string): DynamicStore | undefined {
    for (const store of this.stores.values()) {
      if (store.storeId === storeId || store.callbackReturnPath === storeId) {
        return store;
      }
    }
    // Also try by path
    return this.stores.get(storeId);
  }

  destroyAllStores(): void {
    this.reactions.forEach((dispose) => {
      dispose();
    });
    this.reactions.clear();

    this.stores.forEach((store) => {
      if (store.destroy) {
        store.destroy();
      }
    });
    this.stores.clear();
  }

  get allPaths(): string[] {
    return Array.from(this.stores.keys());
  }

  get allStores(): DynamicStore[] {
    return Array.from(this.stores.values());
  }

  get storeCount(): number {
    return this.stores.size;
  }
}
